import { useEffect, useRef, useCallback, useState } from 'react';
import { useAudioPlayback } from '../hooks/useAudioPlayback';
import { GameStatsOverlay } from './GameStatsOverlay';
import type { ServerMessage } from '@stream-buddy/shared';
import { logger } from '../utils/logger';


interface GameStatEntry {
  value: number;
  label: string;
  updatedAt: number;
  lastUpdateMetadata?: string;
}

// Reconnection settings
const INITIAL_RECONNECT_DELAY = 1000; // 1 second
const MAX_RECONNECT_DELAY = 10000; // 10 seconds (reduced from 30)
const WAITING_RECONNECT_DELAY = 5000; // 5 seconds when waiting for Go Live (reduced from 60)
const RECONNECT_BACKOFF_MULTIPLIER = 1.5;
const PING_INTERVAL = 15000; // 15 seconds (reduced from 25 for faster stale detection)
const PONG_TIMEOUT = 5000; // If no pong within 5 seconds, connection is stale
const CONNECTION_CHECK_INTERVAL = 10000; // Check every 10 seconds if we should reconnect

export function Overlay() {
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<'connecting' | 'connected' | 'waiting' | 'error' | 'auth_failed'>('connecting');
  const [visualsEnabled, setVisualsEnabled] = useState(true);
  const [overlayScale, setOverlayScale] = useState(2.0);
  const activeTurnIdRef = useRef<string | null>(null);
  const activeTurnSeqRef = useRef<number | null>(null);
  const isWaitingRef = useRef(false); // Track if session is waiting for Go Live
  const discardAudioUntilRef = useRef<number>(0); // Timestamp to discard audio until (for interrupt handling)
  const audioDiscardGenerationRef = useRef(0);
  const awaitingPongRef = useRef(false); // Track if we're waiting for a pong response
  const pongTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Timeout for pong response
  const [gameStats, setGameStats] = useState<Record<string, GameStatEntry>>(() => {
    const search = window.location.search;
    if (search.includes('test=true')) {
      return {
        wins: { value: 3, label: 'Victories', updatedAt: Date.now() },
        deaths: { value: 12, label: 'Deaths', updatedAt: Date.now() - 5000, lastUpdateMetadata: 'Killed by Lava' },
        loot: { value: 5, label: 'Legendary Loot', updatedAt: Date.now() - 10000, lastUpdateMetadata: 'Excalibur' },
        skill_shots: { value: 42, label: 'Headshots', updatedAt: Date.now() - 15000 },
      };
    }
    const emptyStats: Record<string, GameStatEntry> = {};
    return emptyStats;
  });

  // Get token and channel from URL params
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const isTestMode = params.get('test') === 'true';
  // Handle potential double-question mark issue (user appending ?scale=2 instead of &scale=2)
  const rawWidgetToken = params.get('widgetToken') || params.get('widget_token');
  const widgetToken = rawWidgetToken ? rawWidgetToken.split('?')[0] : null;

  const rawToken = params.get('token');
  const token = rawToken ? rawToken.split('?')[0] : null;
  const channel = params.get('channel');

  const handlePlaybackStateChange = useCallback((state: 'started' | 'ended' | 'cleared') => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'playback_state',
      state,
      turnId: activeTurnIdRef.current ?? undefined,
      turnSeq: activeTurnSeqRef.current ?? undefined,
    }));

    if (state === 'ended' || state === 'cleared') {
      activeTurnIdRef.current = null;
      activeTurnSeqRef.current = null;
    }
  }, []);

  const { playAudio, reset, init, setVolume, setAudioEffect, unlockAudio } = useAudioPlayback({
    onPlaybackStateChange: handlePlaybackStateChange,
  });

  // Pre-initialize audio on mount so it's ready for first audio chunk
  useEffect(() => {
    void init();
  }, [init]);

  const handleMessage = useCallback((event: MessageEvent) => {
    // Check if binary message (audio)
    if (event.data instanceof Blob) {
      const audioGenerationAtReceive = audioDiscardGenerationRef.current;
      // Discard audio that arrives right after an interrupt (stale audio in-flight)
      if (Date.now() < discardAudioUntilRef.current) {
        logger.log('[Overlay] Discarding stale audio after interrupt');
        return;
      }
      void event.data.arrayBuffer().then((buffer) => {
        if (audioGenerationAtReceive !== audioDiscardGenerationRef.current || Date.now() < discardAudioUntilRef.current) {
          logger.log('[Overlay] Discarding stale audio after async conversion');
          return;
        }
        void playAudio(buffer).catch((error) => {
          console.error('[Overlay] playAudio failed:', error);
        });
      });
      return;
    }

    try {
      const message = JSON.parse(event.data as string) as ServerMessage;

      switch (message.type) {
        case 'audio_output':
          console.warn('[Overlay] Received legacy base64 audio_output');
          break;

        case 'session_restarted':
          logger.log('[Overlay] Session restarted, resetting audio playback state');
          reset();
          break;

        case 'clear_audio':
          logger.log('[Overlay] Streamer interrupted, clearing audio buffer');
          reset();
          audioDiscardGenerationRef.current += 1;
          discardAudioUntilRef.current = Date.now() + 500;
          break;

        case 'pong':
          awaitingPongRef.current = false;
          if (pongTimeoutRef.current) {
            clearTimeout(pongTimeoutRef.current);
            pongTimeoutRef.current = null;
          }
          break;

        case 'status': {
          if (message.waiting) {
            logger.log('[Overlay] Session not started yet, waiting for Go Live');
            isWaitingRef.current = true;
            setStatus('waiting');
          } else if (message.connected) {
            logger.log('[Overlay] Session is active - attempting to unlock audio');
            isWaitingRef.current = false;
            setStatus('connected');
            void unlockAudio().then(() => {
              logger.log('[Overlay] Audio unlocked successfully');
            }).catch((err) => {
              console.error('[Overlay] Failed to unlock audio:', err);
            });
          }
          if (message.visualsEnabled !== undefined) {
            setVisualsEnabled(message.visualsEnabled);
          }
          if (message.overlayScale !== undefined) {
            setOverlayScale(message.overlayScale);
            logger.log('[Overlay] Initial scale:', message.overlayScale);
          }
          break;
        }

        case 'visuals_changed': {
          setVisualsEnabled(message.enabled);
          logger.log('[Overlay] Visuals updated to:', message.enabled);
          break;
        }

        case 'scale_changed': {
          setOverlayScale(message.scale);
          logger.log('[Overlay] Scale updated to:', message.scale);
          break;
        }

        case 'volume_changed': {
          setVolume(message.volume);
          logger.log('[Overlay] Volume updated to:', message.volume);
          break;
        }

        case 'audio_effect_changed': {
          setAudioEffect(message.mode, message.intensity);
          logger.log('[Overlay] Audio effect changed:', message.mode, 'intensity:', message.intensity);
          break;
        }

        case 'session_stats': {
          if (message.gameStats) {
            setGameStats(message.gameStats as Record<string, GameStatEntry>);
          }
          break;
        }

        case 'turn_event': {
          if (typeof message.turnId === 'string') {
            activeTurnIdRef.current = message.turnId;
          }
          if (typeof message.turnSeq === 'number') {
            activeTurnSeqRef.current = message.turnSeq;
          }
          if (message.event === 'turn_complete' || message.event === 'turn_failed') {
            activeTurnIdRef.current = null;
            activeTurnSeqRef.current = null;
          }
          break;
        }

        case 'speaking_state':
        case 'chat_alert':
        case 'transcript':
          break;

        default: {
          const _: { type: string } = message;
          console.warn('[Overlay] Unhandled message type:', _.type);
        }
      }
    } catch (e) {
      console.error('[Overlay] Failed to parse message:', e);
    }
  }, [playAudio, reset, setVolume, setAudioEffect, unlockAudio]);

  // Store handler in ref to avoid re-creating WebSocket on handler changes
  const handleMessageRef = useRef(handleMessage);

  // Keep ref up to date
  useEffect(() => {
    handleMessageRef.current = handleMessage;
  });

  useEffect(() => {
    if (isTestMode) {
      logger.log('[Overlay] Test Mode enabled, skipping server connection');
      return;
    }

    // Need either a widget token OR (legacy token + channel)
    if (!widgetToken && (!token || !channel)) {
      console.error('[Overlay] Missing widgetToken or token+channel in URL');
      return;
    }

    let mounted = true;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let pingInterval: ReturnType<typeof setInterval> | null = null;
    let connectionCheckInterval: ReturnType<typeof setInterval> | null = null;
    let reconnectDelay = INITIAL_RECONNECT_DELAY;
    let isConnecting = false; // Prevent multiple simultaneous connection attempts

    const stopPing = () => {
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      if (pongTimeoutRef.current) {
        clearTimeout(pongTimeoutRef.current);
        pongTimeoutRef.current = null;
      }
      awaitingPongRef.current = false;
    };

    const startPing = (ws: WebSocket) => {
      stopPing();
      pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          // If we're still waiting for a pong from last ping, connection is stale
          if (awaitingPongRef.current) {
            logger.log('[Overlay] Ping timeout - no pong received, forcing reconnect');
            ws.close(4000, 'Ping timeout');
            return;
          }

          awaitingPongRef.current = true;
          ws.send(JSON.stringify({ type: 'ping' }));

          // Set a timeout for the pong response
          pongTimeoutRef.current = setTimeout(() => {
            if (awaitingPongRef.current && ws.readyState === WebSocket.OPEN) {
              logger.log('[Overlay] Pong timeout - connection may be stale');
              // Don't close immediately, let next ping cycle handle it
            }
          }, PONG_TIMEOUT);
        }
      }, PING_INTERVAL);
    };

    const connect = () => {
      if (!mounted) return;
      if (isConnecting) {
        logger.log('[Overlay] Already connecting, skipping...');
        return;
      }
      isConnecting = true;

      // Clean up existing connection if any
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const viteApiHost = import.meta.env.VITE_API_HOST as string | undefined;
      const wsHost = window.location.host !== '' && !window.location.host.includes('localhost')
        ? window.location.host
        : (viteApiHost || 'localhost:3001');

      // Build URL with either widget token (preferred) or legacy JWT token
      let wsUrl: string;
      if (widgetToken) {
        wsUrl = `${wsProtocol}//${wsHost}/ws?widgetToken=${widgetToken}&type=overlay`;
      } else {
        wsUrl = `${wsProtocol}//${wsHost}/ws?token=${token}&channel=${channel}&type=overlay`;
      }
      logger.log('[Overlay] Connecting to:', wsUrl.replace(/(?:widgetToken|token)=[^&]+/, '$&=***'));

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // Connection timeout - if we don't connect within 10 seconds, close and retry
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          logger.log('[Overlay] Connection timeout, closing and retrying...');
          ws.close();
        }
      }, 10000);

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        logger.log('[Overlay] Connected to server');
        setStatus('connected');
        isConnecting = false;
        // Reset reconnect delay on successful connection
        reconnectDelay = INITIAL_RECONNECT_DELAY;
        // Clear any pending reconnect timeout
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
          reconnectTimeout = null;
        }
        // Start ping interval to keep connection alive
        startPing(ws);
      };

      ws.onclose = (event) => {
        logger.log('[Overlay] Disconnected from server', event.code, event.reason);
        stopPing();
        isConnecting = false;

        // Reset waiting state on disconnect - we'll get fresh status on reconnect
        isWaitingRef.current = false;

        // Don't reconnect on auth failures (4001 = no token, 4002 = invalid token, 4003 = subscription expired)
        if (event.code === 4001 || event.code === 4002 || event.code === 4003) {
          logger.log('[Overlay] Authentication failed, not reconnecting');
          setStatus('auth_failed');
          return;
        }

        setStatus('connecting');
        // Attempt to reconnect if component is still mounted
        if (mounted) {
          // Clear any existing reconnect timeout to prevent duplicates
          if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
            reconnectTimeout = null;
          }

          // Use longer delay when session is waiting for Go Live (no need for aggressive reconnection)
          const effectiveDelay = isWaitingRef.current ? WAITING_RECONNECT_DELAY : reconnectDelay;
          logger.log(`[Overlay] Reconnecting in ${effectiveDelay}ms...${isWaitingRef.current ? ' (waiting for Go Live)' : ''}`);

          reconnectTimeout = setTimeout(() => {
            // Only increase delay for exponential backoff when NOT in waiting state
            if (!isWaitingRef.current) {
              reconnectDelay = Math.min(
                reconnectDelay * RECONNECT_BACKOFF_MULTIPLIER,
                MAX_RECONNECT_DELAY
              );
            }
            connect();
          }, effectiveDelay);
        }
      };

      ws.onerror = (error) => {
        console.error('[Overlay] WebSocket error:', error);
        // Don't set error status here - let onclose handle reconnection
        // Setting status to 'error' was preventing the UI from showing 'connecting' during reconnect
      };

      ws.onmessage = (event) => handleMessageRef.current(event);
    };

    connect();

    // Periodic connection check - catches cases where onclose doesn't fire
    // or reconnect timer fails (e.g., OBS browser source suspended)
    connectionCheckInterval = setInterval(() => {
      if (!mounted) return;

      const ws = wsRef.current;
      const needsReconnect = !ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING;

      if (needsReconnect && !isConnecting && !reconnectTimeout) {
        logger.log('[Overlay] Connection check: not connected, initiating reconnect...');
        connect();
      }
    }, CONNECTION_CHECK_INTERVAL);

    // Visibility change handler - reconnect when page becomes visible
    // (helps with OBS browser sources that get suspended)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && mounted) {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          logger.log('[Overlay] Page became visible, checking connection...');
          connect();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;

      // Clear reconnect timeout
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      // Stop connection check interval
      if (connectionCheckInterval) {
        clearInterval(connectionCheckInterval);
      }

      // Remove visibility listener
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      // Stop ping interval
      stopPing();

      // Close WebSocket
      logger.log('[Overlay] Unmounting, disconnecting...');
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [widgetToken, token, channel, isTestMode]); // Reconnect if any auth params change

  // Show debug info if not connected (visible in OBS for debugging)
  // Check for ?test=true or ?debug=true in URL
  const showDebug = search.includes('debug=true');

  // Stats are initialized eagerly in useState above when in test mode.

  // Render overlay components
  return (
    <>
      {visualsEnabled && <GameStatsOverlay stats={gameStats} scale={overlayScale} />}

      {showDebug && status !== 'connected' && (
        <div style={{
          position: 'fixed',
          top: 10,
          left: 10,
          background: status === 'error' || status === 'auth_failed' ? '#ff4444' : status === 'waiting' ? '#6b46c1' : '#333',
          color: 'white',
          padding: '8px 12px',
          borderRadius: 4,
          fontSize: 12,
          fontFamily: 'monospace'
        }}>
          {status === 'connecting' && '⏳ Connecting...'}
          {status === 'waiting' && '⏸️ Waiting for Go Live...'}
          {status === 'error' && '❌ Connection Error'}
          {status === 'auth_failed' && '🔒 Auth Failed - Check Token'}
        </div>
      )}
    </>
  );
}
